import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import venueLocationService from '../../services/venueLocationService';
import stockCountService from '../../services/stockCountService';
import type { VenueLocation } from '../../types/venue';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  FileDownload as DownloadIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarnIcon,
} from '@mui/icons-material';
import inventoryImportService, {
  extractInventoryImportError,
  downloadExistingItemsAsImport,
  type InventoryImportPreviewResponse,
  type InventoryImportPreviewRow,
  type InventoryImportCommitResponse,
  type InventoryImportCommitRow,
  type ImportAction,
  type CommitStatus,
} from '../../services/inventoryImportService';
import { downloadInventoryImportTemplate } from '../../utils/inventoryImportTemplate';
import { humanizeItemType } from '../../utils/inventoryItemDisplay';
import { downloadImportPreviewReport, joinIssues, type ReportColumn } from '../../utils/importPreviewReport';

// ── Opening stock that has to go through a count ─────────────────────────────

/** IMPORT-ROUNDTRIP.1 — refused opening rows for one location, ready for a count. */
export interface CountCandidateGroup {
  locationId: string;
  lines: Array<{ inventoryItemId: string; countedQty: number }>;
}

/**
 * IMPORT-ROUNDTRIP.1 — the rows whose Opening Qty was refused because the item
 * already moved at that location, grouped by location (a count covers one).
 * The last row wins when a file names an item twice for one location, as the
 * server does.
 */
export function collectCountCandidates(rows: InventoryImportPreviewRow[]): CountCandidateGroup[] {
  const byLocation = new Map<string, Map<string, number>>();
  for (const r of rows) {
    const { openingBlockedByActivity, existingItemId, openingLocationId } = r.resolved;
    const qty = r.data.openingQty;
    if (!openingBlockedByActivity || !existingItemId || !openingLocationId || qty == null) continue;
    const lines = byLocation.get(openingLocationId) ?? new Map<string, number>();
    lines.set(existingItemId, qty);
    byLocation.set(openingLocationId, lines);
  }
  return Array.from(byLocation.entries()).map(([locationId, lines]) => ({
    locationId,
    lines: Array.from(lines.entries()).map(([inventoryItemId, countedQty]) => ({ inventoryItemId, countedQty })),
  }));
}

/** Today in the user's own calendar — toISOString() would give UTC's date. */
const localToday = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'idle' | 'previewing' | 'previewed' | 'committing' | 'committed';

interface Props {
  open: boolean;
  venueId: string;
  onClose: () => void;
  onSuccess: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function rowLabel(row: InventoryImportPreviewRow): string {
  return (
    row.resolved.inventoryDisplayName ||
    row.data.itemName ||
    `Row ${row.rowNumber}`
  );
}

function commitRowLabel(
  row: InventoryImportCommitRow,
  preview: InventoryImportPreviewRow | undefined,
): string {
  if (preview) return rowLabel(preview);
  return `Row ${row.rowNumber}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ActionChip({ action }: { action: ImportAction }) {
  const cfg = {
    CREATE: { label: 'Create', color: '#1a7f37', bg: '#dafbe1' },
    UPDATE: { label: 'Update', color: '#0969da', bg: '#ddf4ff' },
    ERROR:  { label: 'Error',  color: '#cf222e', bg: '#ffebe9' },
  }[action];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.68rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 20 }}
    />
  );
}

function StatusChip({ status }: { status: CommitStatus }) {
  const cfg = {
    SUCCESS: { label: 'Success', color: '#1a7f37', bg: '#dafbe1' },
    ERROR:   { label: 'Error',   color: '#cf222e', bg: '#ffebe9' },
    SKIPPED: { label: 'Skipped', color: '#57606a', bg: '#f6f8fa' },
  }[status];
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.68rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 20 }}
    />
  );
}

function SummaryChips({
  items,
}: {
  items: { label: string; value: number; color: string; bg: string }[];
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {items.map(item => (
        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip
            label={item.value}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '0.78rem',
              color: item.color,
              backgroundColor: item.bg,
              border: 'none',
              minWidth: 28,
              height: 22,
            }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Preview table ─────────────────────────────────────────────────────────────

function PreviewTable({ rows }: { rows: InventoryImportPreviewRow[] }) {
  const theme = useTheme();
  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Row', 'Action', 'Item', 'Supplier', 'Menu', 'Issues'].map(h => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: alpha(theme.palette.text.primary, 0.45),
                  backgroundColor: theme.palette.background.paper,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.rowNumber}
              sx={{
                '& td': { borderColor: theme.palette.divider },
                backgroundColor:
                  row.action === 'ERROR' ? alpha('#cf222e', 0.03) : undefined,
              }}
            >
              <TableCell sx={{ py: 0.75, color: 'text.secondary', fontSize: '0.75rem', width: 42 }}>
                {row.rowNumber}
              </TableCell>
              <TableCell sx={{ py: 0.75, width: 70 }}>
                <ActionChip action={row.action} />
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 220 }}>
                <Typography
                  fontSize="0.78rem"
                  noWrap
                  fontWeight={row.action !== 'ERROR' ? 500 : 400}
                >
                  {rowLabel(row)}
                </Typography>
                {row.data.itemType && (
                  <Typography fontSize="0.67rem" color="text.disabled">
                    {humanizeItemType(row.data.itemType)}
                    {row.data.sizeValue
                      ? ` · ${row.data.sizeValue}${row.data.sizeUnit ?? ''}`
                      : ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 140 }}>
                {row.data.supplierName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography fontSize="0.75rem" color="text.secondary" noWrap>
                      {row.data.supplierName}
                    </Typography>
                    {row.resolved.supplierAction === 'AUTO_CREATE' && (
                      <Tooltip title="Will be created automatically on confirm">
                        <Chip
                          label="New"
                          size="small"
                          sx={{
                            fontSize: '0.6rem',
                            height: 16,
                            fontWeight: 600,
                            color: '#9a6700',
                            backgroundColor: '#fef9c3',
                            border: 'none',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography fontSize="0.75rem" color="text.disabled">—</Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 120 }}>
                {row.data.menuName ? (
                  <Box>
                    <Typography fontSize="0.75rem" color="text.secondary" noWrap>
                      {row.data.menuSection
                        ? `${row.data.menuName} › ${row.data.menuSection}`
                        : row.data.menuName}
                    </Typography>
                    {row.resolved.sectionWillBeCreated && (
                      <Chip
                        label="New section"
                        size="small"
                        sx={{ fontSize: '0.6rem', height: 16, mt: 0.25, color: '#9a6700', backgroundColor: '#fef9c3', border: 'none' }}
                      />
                    )}
                  </Box>
                ) : (
                  <Typography fontSize="0.75rem" color="text.disabled">—</Typography>
                )}
              </TableCell>
              <TableCell sx={{ py: 0.75, maxWidth: 260 }}>
                {row.errors.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {row.errors.map((e, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <ErrorIcon sx={{ fontSize: 12, color: '#cf222e', mt: 0.25, flexShrink: 0 }} />
                        <Typography fontSize="0.72rem" color="#cf222e" sx={{ lineHeight: 1.4 }}>
                          {e}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {row.errors.length === 0 && row.warnings.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <WarnIcon sx={{ fontSize: 12, color: '#9a6700', mt: 0.25, flexShrink: 0 }} />
                    <Typography fontSize="0.72rem" color="#9a6700" sx={{ lineHeight: 1.4 }}>
                      {row.warnings[0]}
                    </Typography>
                  </Box>
                )}
                {row.errors.length === 0 && row.warnings.length === 0 && (
                  <CheckIcon sx={{ fontSize: 14, color: '#1a7f37' }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Commit result table ───────────────────────────────────────────────────────

function CommitTable({
  rows,
  previewRows,
}: {
  rows: InventoryImportCommitRow[];
  previewRows: InventoryImportPreviewRow[];
}) {
  const theme = useTheme();
  const previewMap = new Map(previewRows.map(r => [r.rowNumber, r]));

  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Row', 'Status', 'Action', 'Item', 'Details', 'Errors'].map(h => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: alpha(theme.palette.text.primary, 0.45),
                  backgroundColor: theme.palette.background.paper,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const preview = previewMap.get(row.rowNumber);
            return (
              <TableRow
                key={row.rowNumber}
                sx={{
                  '& td': { borderColor: theme.palette.divider },
                  backgroundColor:
                    row.status === 'ERROR'
                      ? alpha('#cf222e', 0.03)
                      : row.status === 'SKIPPED'
                        ? alpha('#57606a', 0.03)
                        : undefined,
                }}
              >
                <TableCell sx={{ py: 0.75, color: 'text.secondary', fontSize: '0.75rem', width: 42 }}>
                  {row.rowNumber}
                </TableCell>
                <TableCell sx={{ py: 0.75, width: 76 }}>
                  <StatusChip status={row.status} />
                </TableCell>
                <TableCell sx={{ py: 0.75, width: 70 }}>
                  {row.action !== 'ERROR' ? (
                    <ActionChip action={row.action} />
                  ) : (
                    <Typography fontSize="0.72rem" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 0.75, maxWidth: 220 }}>
                  <Typography fontSize="0.78rem" noWrap>
                    {commitRowLabel(row, preview)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.75, maxWidth: 160 }}>
                  {row.status === 'SUCCESS' && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.result.supplierCreated && (
                        <Chip
                          label="Supplier created"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16, fontWeight: 600, color: '#0969da', backgroundColor: '#ddf4ff', border: 'none' }}
                        />
                      )}
                      {row.result.supplierLinked && !row.result.supplierCreated && (
                        <Chip
                          label="Supplier linked"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16, color: '#57606a', backgroundColor: '#f6f8fa', border: 'none' }}
                        />
                      )}
                      {row.result.menuLinked && (
                        <Chip
                          label="Wine list linked"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16, color: '#6941c6', backgroundColor: alpha('#6941c6', 0.08), border: 'none' }}
                        />
                      )}
                      {!row.result.supplierLinked && !row.result.menuLinked && (
                        <Typography fontSize="0.72rem" color="text.disabled">Item only</Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ py: 0.75, maxWidth: 240 }}>
                  {row.errors.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {row.errors.map((e, idx) => (
                        <Typography key={idx} fontSize="0.72rem" color="#cf222e" sx={{ lineHeight: 1.4 }}>
                          {e}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

const InventoryImportDialog: React.FC<Props> = ({ open, venueId, onClose, onSuccess }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('idle');
  const [file, setFile] = useState<File | null>(null);
  /**
   * IMPORT-DESTINATION.1 — where opening stock lands for rows that name no
   * location of their own.
   *
   * Before this the fallback was the venue's DEFAULT location, so importing
   * into a shared cellar could only be expressed by repeating its name down
   * every row — and there was nowhere on this screen to say it, which is the
   * one screen built for doing something in bulk.
   */
  const [destinationLocationId, setDestinationLocationId] = useState<string>('');
  const [locations, setLocations] = useState<VenueLocation[]>([]);

  useEffect(() => {
    if (!open || !venueId) return;
    // RECEIVE, not VIEW: this picker chooses where stock will be POSTED, so it
    // must not offer a cellar the venue may only sell from — that would be a
    // choice that fails on commit, after the file was already parsed.
    venueLocationService.getLocations(venueId, 'RECEIVE')
      .then(locs => {
        setLocations(locs);
        // Default to the venue's own default, which is exactly what the import
        // did before this control existed.
        setDestinationLocationId(prev => prev || (locs.find(l => l.isDefault)?._id ?? ''));
      })
      .catch(() => setLocations([]));
  }, [open, venueId]);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<InventoryImportPreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<InventoryImportCommitResponse | null>(null);
  const [exportingItems, setExportingItems] = useState(false);
  /** IMPORT-ROUNDTRIP.1 — counts started from refused opening rows, by location. */
  const [countsStarted, setCountsStarted] = useState<Record<string, { countId: string; added: number; updated: number; skipped: number }>>({});
  const [startingCountFor, setStartingCountFor] = useState<string | null>(null);
  // Preview-table filter chips: 'all' shows everything, others narrow by action.
  // The "Download Report" button always emits ALL preview rows regardless.
  const [previewFilter, setPreviewFilter] = useState<'all' | 'CREATE' | 'UPDATE' | 'ERROR'>('all');

  const didCommitSuccessfully =
    commitResult !== null &&
    (commitResult.summary.created + commitResult.summary.updated) > 0;

  const handleClose = () => {
    if (step === 'previewing' || step === 'committing') return;
    if (didCommitSuccessfully) onSuccess();
    onClose();
    setTimeout(() => {
      setStep('idle');
      setFile(null);
      setError(null);
      setPreview(null);
      setCommitResult(null);
      setCountsStarted({});
    }, 300);
  };

  const locationLabel = (id: string) => {
    const loc = locations.find(l => l._id === id);
    return loc ? (loc.displayName ?? loc.name) : 'that location';
  };

  /**
   * IMPORT-ROUNDTRIP.1 — put refused opening quantities into a draft count.
   * Does not navigate: the user may still want to confirm the rest of the
   * import first. The count opens from the link that appears once it exists.
   */
  const handleStartCount = async (group: CountCandidateGroup) => {
    setStartingCountFor(group.locationId);
    setError(null);
    try {
      const res = await stockCountService.prefillCount(venueId, {
        locationId: group.locationId,
        countedAt: localToday(),
        lines: group.lines,
      });
      setCountsStarted(prev => ({
        ...prev,
        [group.locationId]: { countId: res.countId, added: res.added, updated: res.updated, skipped: res.skipped.length },
      }));
    } catch (err) {
      setError(extractInventoryImportError(err));
    } finally {
      setStartingCountFor(null);
    }
  };

  const openCount = (countId: string) => {
    handleClose();
    navigate(`/admin/venues/${venueId}/stock-counts/${countId}`);
  };

  const handleChooseFile = () => {
    // Reset before opening so the user can re-pick the same file and get a fresh onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
    setPreview(null);
    setStep('idle');
  };

  const handleDownloadExisting = async () => {
    if (exportingItems) return;
    setExportingItems(true);
    try {
      // IMPORT-ROUNDTRIP.1 — for the location this file is for: its own Par /
      // Reorder / Bin, what is on hand there, and its name in Location.
      const rows = await inventoryImportService.exportItems(venueId, destinationLocationId || null);
      downloadExistingItemsAsImport(rows, destinationLocationId ? locationLabel(destinationLocationId) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export existing items.');
    } finally {
      setExportingItems(false);
    }
  };

  /**
   * Download the FULL preview as XLSX so admins can scroll past the in-dialog
   * window and inspect every row. Always emits all rows (not the filtered set)
   * because the report is meant to be a complete picture.
   */
  const handleDownloadPreviewReport = () => {
    if (!preview) return;
    const cols: ReportColumn<InventoryImportPreviewRow>[] = [
      { header: 'Row Number', value: r => r.rowNumber },
      { header: 'Action',     value: r => r.action },
      { header: 'Item Type',  value: r => r.data.itemType ?? '' },
      { header: 'Item Name',  value: r => r.resolved.inventoryDisplayName ?? r.data.itemName ?? '' },
      { header: 'Size',       value: r => (r.data.sizeValue != null && r.data.sizeUnit) ? `${r.data.sizeValue} ${r.data.sizeUnit}` : '' },
      { header: 'Supplier',   value: r => r.data.supplierName ?? '' },
      { header: 'Supplier Action', value: r => r.resolved.supplierAction ?? '' },
      { header: 'Menu', value: r => r.data.menuName ?? '' },
      { header: 'Menu Section', value: r => r.data.menuSection ?? '' },
      { header: 'Default Location?', value: r => r.resolved.willUseDefaultLocation ? 'yes' : '' },
      { header: 'Existing Item ID', value: r => r.resolved.existingItemId ?? '' },
      { header: 'Errors',     value: r => joinIssues(r.errors) },
      { header: 'Warnings',   value: r => joinIssues(r.warnings) },
    ];
    downloadImportPreviewReport('inventory-import-preview-report.xlsx', cols, preview.rows);
  };

  const handlePreview = async () => {
    if (!file) return;
    setStep('previewing');
    setError(null);
    try {
      const result = await inventoryImportService.preview(venueId, file, destinationLocationId || null);
      setPreview(result);
      setStep('previewed');
    } catch (err) {
      setError(extractInventoryImportError(err));
      setStep('idle');
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setStep('committing');
    setError(null);
    try {
      const result = await inventoryImportService.commit(venueId, file, destinationLocationId || null);
      setCommitResult(result);
      setStep('committed');
    } catch (err) {
      setError(extractInventoryImportError(err));
      setStep('previewed');
    }
  };

  const canCommit =
    step === 'previewed' &&
    preview !== null &&
    preview.summary.create + preview.summary.update > 0;

  const isBusy = step === 'previewing' || step === 'committing';

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, minHeight: 340 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>
          {step === 'committed' ? 'Import Complete' : 'Import Inventory Items'}
        </Typography>
        <IconButton size="small" onClick={handleClose} disabled={isBusy}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {/* Single hidden file input — always mounted so ref stays valid */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* ── Error banner ────────────────────────────────────────────── */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── IDLE — file selection ─────────────────────────────────── */}
        {step === 'idle' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an <strong>.xlsx</strong> file to preview and import inventory items.
              Use the template to ensure columns are correct, or export your current items as a starting point.
            </Typography>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Selling Price</strong> is the inventory item&apos;s own default / POS selling
                price, entered in normal currency units (e.g. <strong>95.00</strong>). It belongs to
                the item — not to a menu — and is stored internally in minor units (cents).
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Menu Name</strong> places a wine on an existing Menu. It is optional and does
                not set or own the price. <strong>Menu Section</strong> (optional) places it into that
                named section of the menu — a section that doesn&apos;t exist yet is created on confirm.
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                A blank <strong>Bin Location</strong>, <strong>Par Level</strong> or{' '}
                <strong>Reorder Point</strong> leaves the current value as it is. Type{' '}
                <strong>-</strong> to clear one.
              </Typography>
              <Typography variant="body2">
                <strong>Opening Qty</strong> is the first stock of an item at a location. An item that
                has already sold or been received there can&apos;t take one — the preview offers to put
                those quantities into a stock count instead.
              </Typography>
            </Alert>

            {/* IMPORT-DESTINATION.1 — the one thing this screen could not say. */}
            <FormControl fullWidth size="small">
              <InputLabel>Location this file is for</InputLabel>
              <Select
                value={destinationLocationId}
                label="Location this file is for"
                onChange={e => setDestinationLocationId(e.target.value)}
              >
                {locations.map(loc => (
                  <MenuItem key={loc._id} value={loc._id}>{loc.displayName ?? loc.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Where each row&apos;s Par Level, Reorder Point, Bin Location and Opening Qty go. A row
                that fills in its own <strong>Location</strong> goes there instead, so one file can
                still cover several places.
              </FormHelperText>
            </FormControl>

            {/* Download actions */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Downloads
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadInventoryImportTemplate}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Download Template
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exportingItems ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={handleDownloadExisting}
                  disabled={exportingItems}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {exportingItems ? 'Exporting…' : 'Start from my current items'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Every item, with this location&apos;s Par, Reorder and Bin and what&apos;s on hand there.
                Edit it and upload it here. An import takes up to 500 rows at a time.
              </Typography>
            </Box>

            <Divider />

            {/* File upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Upload
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={handleChooseFile}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {file ? 'Change File' : 'Choose File (.xlsx / .csv)'}
                </Button>
                {file && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {file.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── PREVIEWING spinner ─────────────────────────────────────── */}
        {step === 'previewing' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">Analysing file…</Typography>
          </Box>
        )}

        {/* ── PREVIEW results ────────────────────────────────────────── */}
        {step === 'previewed' && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File line + change */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {file?.name}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={handleChooseFile}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Change file
              </Button>
            </Box>

            {/* Summary box */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Preview — {preview.totalRows} {preview.totalRows === 1 ? 'row' : 'rows'}
              </Typography>
              <SummaryChips items={[
                { label: 'will be created', value: preview.summary.create, color: '#1a7f37', bg: '#dafbe1' },
                { label: 'will be updated', value: preview.summary.update, color: '#0969da', bg: '#ddf4ff' },
                { label: 'have errors',     value: preview.summary.error,  color: '#cf222e', bg: '#ffebe9' },
              ]} />
              {preview.summary.error > 0 && preview.summary.create + preview.summary.update === 0 && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                  All rows have errors — fix the file and preview again.
                </Alert>
              )}
              {preview.summary.error > 0 && preview.summary.create + preview.summary.update > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                  {preview.summary.error} {preview.summary.error === 1 ? 'row has' : 'rows have'} errors and will be skipped on import.
                </Alert>
              )}
            </Box>

            {/* IMPORT-ROUNDTRIP.1 — refused opening quantities, one way forward. */}
            {collectCountCandidates(preview.rows).map(group => {
              const started = countsStarted[group.locationId];
              const n = group.lines.length;
              const where = locationLabel(group.locationId);
              return (
                <Alert
                  key={group.locationId}
                  severity={started ? 'success' : 'info'}
                  sx={{ borderRadius: 2 }}
                  action={started ? (
                    <Button color="inherit" size="small" onClick={() => openCount(started.countId)} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Open count
                    </Button>
                  ) : (
                    <Button
                      color="inherit"
                      size="small"
                      disabled={startingCountFor !== null}
                      onClick={() => handleStartCount(group)}
                      sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      {startingCountFor === group.locationId ? 'Starting…' : `Start a stock count with ${n === 1 ? 'this quantity' : `these ${n} quantities`}`}
                    </Button>
                  )}
                >
                  {started ? (
                    <>
                      Draft count at <strong>{where}</strong> has {started.added + started.updated}{' '}
                      {started.added + started.updated === 1 ? 'quantity' : 'quantities'} entered
                      {started.skipped > 0 && <> ({started.skipped} skipped — see the count)</>}.
                      Review and post it to set what&apos;s on hand. Nothing changes until it&apos;s posted.
                    </>
                  ) : (
                    <>
                      {n === 1 ? '1 item' : `${n} items`} at <strong>{where}</strong> already sold or were
                      received there, so their Opening Qty can&apos;t be booked. A stock count can set
                      what&apos;s on hand instead.
                    </>
                  )}
                </Alert>
              );
            })}

            {/* Filter + download row.
                Filter chips narrow the in-dialog table; the report download is
                always the full preview so admins can fix the source file. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {([
                  { key: 'all',    label: `All (${preview.totalRows})` },
                  { key: 'ERROR',  label: `Errors (${preview.summary.error})` },
                  { key: 'CREATE', label: `Created (${preview.summary.create})` },
                  { key: 'UPDATE', label: `Updated (${preview.summary.update})` },
                ] as const).map(chip => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    variant={previewFilter === chip.key ? 'filled' : 'outlined'}
                    color={previewFilter === chip.key ? 'primary' : 'default'}
                    onClick={() => setPreviewFilter(chip.key)}
                    sx={{ fontSize: '0.72rem', height: 24, fontWeight: 600 }}
                  />
                ))}
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPreviewReport}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Download report
              </Button>
            </Box>

            {/* Row table */}
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <PreviewTable
                rows={previewFilter === 'all'
                  ? preview.rows
                  : preview.rows.filter(r => r.action === previewFilter)}
              />
            </Box>
          </Box>
        )}

        {/* ── COMMITTING spinner ─────────────────────────────────────── */}
        {step === 'committing' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">Importing items…</Typography>
          </Box>
        )}

        {/* ── COMMITTED result ───────────────────────────────────────── */}
        {step === 'committed' && commitResult && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha('#1a7f37', 0.04),
                border: `1px solid ${alpha('#1a7f37', 0.18)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Import result — {commitResult.totalRows} {commitResult.totalRows === 1 ? 'row' : 'rows'} processed
              </Typography>
              <SummaryChips items={[
                { label: 'created',          value: commitResult.summary.created, color: '#1a7f37', bg: '#dafbe1' },
                { label: 'updated',          value: commitResult.summary.updated, color: '#0969da', bg: '#ddf4ff' },
                { label: 'errored / skipped', value: commitResult.summary.errored, color: '#57606a', bg: '#f6f8fa' },
              ]} />
            </Box>

            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <CommitTable rows={commitResult.rows} previewRows={preview.rows} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {step === 'idle' && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!file}
              onClick={handlePreview}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Preview Import
            </Button>
          </>
        )}

        {step === 'previewed' && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!canCommit}
              onClick={handleCommit}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Confirm Import
              {canCommit && preview && (
                <Typography component="span" fontSize="0.72rem" sx={{ ml: 0.75, opacity: 0.8 }}>
                  ({preview.summary.create + preview.summary.update} rows)
                </Typography>
              )}
            </Button>
          </>
        )}

        {step === 'committed' && (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InventoryImportDialog;
